import "../Management/TrainingProcess.css";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MdAddTask } from "react-icons/md";
import { VscTasklist } from "react-icons/vsc";
import nexticn from "../Components/Assets/svg/chevron-right-solid.svg";
import backicn from "../Components/Assets/svg/back-chevron-right-solid (1).svg";
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Training Process Component - Manages document upload, training approval workflow
 */
export function TrainingProcess() {
  const navigate = useNavigate();

  // Core state
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // UI visibility controls
  const [showHeaderSection, setShowHeaderSection] = useState(true);

  // Document data
  const [pendingDocs, setPendingDocs] = useState([]);
  const [approvedDocs, setApprovedDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [hasPendingDocs, setHasPendingDocs] = useState(false);
  const [hasApprovedDocs, setHasApprovedDocs] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");

  // UI state for document lists and details
  const [showPendingDocsList, setShowPendingDocsList] = useState(true);
  const [showApprovedDocsList, setShowApprovedDocsList] = useState(true);
  const [showPendingDocDetails, setShowPendingDocDetails] = useState(false);
  const [showApprovedDocDetails, setShowApprovedDocDetails] = useState(false);
  const [isUploadFormOpen, setIsUploadFormOpen] = useState(false);
  const [hasPendingDocuments, setHasPendingDocuments] = useState(false);
  const [hasApprovedDocuments, setHasApprovedDocuments] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [approvedDocuments, setApprovedDocuments] = useState([]);
  const [currentSelectedApprovedDoc, setCurrentSelectedApprovedDoc] =
    useState(null);

  // Scroll references for horizontal scrolling
  const pendingScrollRef = useRef(null);
  const approvedScrollRef = useRef(null);
  const scrollRef = useRef(null);

  // Fetch documents when company profile loads
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/process/documents?companyProfileId=${companyProfile?._id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          // This correctly separates the documents
          const pending = response.data.data.filter(
            (doc) => doc.DocViewer === false
          );
          const approved = response.data.data.filter(
            (doc) => doc.DocViewer === true
          );
          setPendingDocs(pending);
          setHasPendingDocs(pending.length > 0);

          setApprovedDocs(approved);
          setHasApprovedDocs(approved.length > 0);

          // Debug logging to understand document distribution
          console.log("Total documents:", response.data.data.length);
          console.log("Pending documents:", pending.length);
          console.log("Approved documents:", approved.length);
        }
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    };

    if (companyProfile?._id) {
      fetchDocuments();
    }
  }, [companyProfile]);

  // Initialize company profile and handle authentication
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/company/get-company-profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          setCompanyProfile(response.data);
        } else {
          toast.error("Company profile not found", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          navigate("/new-company");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 404) {
          toast.error("Please create a company profile first", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          navigate("/new-company");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Auto-close form after successful submission
  useEffect(() => {
    if (submitStatus === "submitted") {
      handleCloseAssignForm();
    }
  }, [submitStatus]);

  // Document form state
  const [documentForm, setDocumentForm] = useState({
    title: "",
    description: "",
    documentType: "",
    DocViewer: false,
  }); // REMOVED DocId (server auto)
  const [documentFile, setDocumentFile] = useState(null); // NEW

  // File validation state for visual feedback
  const [fileValidation, setFileValidation] = useState({
    isValid: null,
    hasFile: false,
  });

  // Handle text input changes in form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDocumentForm({
      ...documentForm,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle file upload and validation with visual feedback
  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setDocumentFile(null);
      setDocumentForm((f) => ({ ...f, documentType: "" }));
      setFileValidation({ isValid: null, hasFile: false });
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    const map = {
      pdf: "pdf",
      doc: "word",
      docx: "word",
      ppt: "pptx",
      pptx: "pptx",
      xls: "excel",
      xlsx: "excel",
      txt: "blog",
    };
    if (!map[ext]) {
      setFileValidation({ isValid: false, hasFile: true });
      toast.error("Invalid file type (pdf/doc/ppt/xls/txt only)", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      e.target.value = "";
      return;
    }
    setDocumentFile(file);
    setDocumentForm((f) => ({ ...f, documentType: map[ext] }));
    setFileValidation({ isValid: true, hasFile: true });
  };

  // Submit document form with file upload
  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    if (submitStatus === "submitting") return;
    if (!documentForm.title.trim()) {
      toast.error("Title required", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }
    if (!documentFile) {
      toast.error("Select a document file", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }
    if (!documentForm.documentType) {
      toast.error("Unsupported file type", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }
    setSubmitStatus("submitting");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Login required", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        navigate("/login");
        setSubmitStatus("idle");
        return;
      }
      const fd = new FormData();
      fd.append("companyProfileId", companyProfile._id);
      fd.append("title", documentForm.title);
      fd.append("description", documentForm.description);
      fd.append("documentType", documentForm.documentType);
      fd.append("DocViewer", documentForm.DocViewer ? "true" : "false");
      fd.append("document", documentFile);

      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/process/docupload`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (resp.data?.success) {
        toast.success("Uploaded", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        setSubmitStatus("submitted");
        // Refresh list
        setTimeout(() => {
          setSubmitStatus("idle");
          setIsUploadFormOpen(false);
          // Trigger refetch by reloading companyProfile dependent effect:
          setPendingDocs((p) => p); // no-op
        }, 900);
      } else {
        throw new Error(resp.data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Error uploading document",
        {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        }
      );
      setSubmitStatus("idle");
    }
  };

  // Form toggle handlers

  // Toggle document upload form visibility
  const toggleAssignForm = () => {
    if (isUploadFormOpen === true) {
      setIsUploadFormOpen(false);
    } else {
      setIsUploadFormOpen(true);
    }
  };

  // Close form and reset all states
  const handleCloseAssignForm = () => {
    setIsUploadFormOpen(false);
    setDocumentForm({
      title: "",
      description: "",
      documentType: "",
      DocViewer: false,
    });
    setDocumentFile(null);
    setSubmitStatus("idle");
    // Reset file validation state
    setFileValidation({
      isValid: null,
      hasFile: false,
    });
    // Also clear the file input
    const documentInput = document.querySelector('input[name="document"]');
    if (documentInput) {
      documentInput.value = "";
    }
  };

  // Compatibility aliases for existing JSX
  const taskForm = documentForm;
  const taskHandleSubmit = handleDocumentSubmit;

  // Scroll handlers for pending documents
  // Scroll left in pending documents carousel
  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -350,
        behavior: "smooth",
      });
    }
  };

  // Scroll right in pending documents carousel
  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 350,
        behavior: "smooth",
      });
    }
  };

  // Scroll handlers for approved documents
  // Scroll left in approved documents carousel
  const approvedScrollLeft = () => {
    if (approvedScrollRef.current) {
      approvedScrollRef.current.scrollBy({
        left: -350,
        behavior: "smooth",
      });
    }
  };

  // Scroll right in approved documents carousel
  const approvedScrollRight = () => {
    if (approvedScrollRef.current) {
      approvedScrollRef.current.scrollBy({
        left: 350,
        behavior: "smooth",
      });
    }
  };

  // Sync state variables for UI display
  useEffect(() => {
    setPendingDocuments(pendingDocs);
    setHasPendingDocuments(hasPendingDocs);
    setApprovedDocuments(approvedDocs);
    setHasApprovedDocuments(hasApprovedDocs);
  }, [pendingDocs, hasPendingDocs, approvedDocs, hasApprovedDocs]);

  // Helper function to get user-friendly display name for document type
  // Convert document type enum to user-friendly display name
  const getDocumentTypeDisplayName = (documentType) => {
    const displayNames = {
      pdf: "PDF Document",
      word: "Word Document",
      pptx: "PowerPoint Presentation",
      excel: "Excel Spreadsheet",
      blog: "Text File",
    };
    return displayNames[documentType] || documentType;
  };

  // Loading state
  if (loading || !companyProfile) {
    return (
      <div className="loader-container">
        <span className="loader"></span>
      </div>
    );
  }

  return (
    <div className="content-management-page">
      {/* Main header */}
      {showHeaderSection && (
        <div className="process-task-title-and-new-task">
          <div className="process-task-title">Document📄</div>
          <div
            className={`new-task-button${
              showPendingDocDetails ? " disabled" : ""
            }`}
            onClick={() => {
              if (isUploadFormOpen === false) {
                setIsUploadFormOpen(true);
                setShowPendingDocsList(false);
                setShowPendingDocDetails(false);
              } else {
                setIsUploadFormOpen(false);
                setShowPendingDocsList(true);
                setShowPendingDocDetails(false);
              }
            }}
            style={{
              pointerEvents: showPendingDocDetails === true ? "none" : "auto",
              opacity: showPendingDocDetails === true ? 0.5 : 1,
              cursor:
                showPendingDocDetails === true ? "not-allowed" : "pointer",
            }}
          >
            + Uploade Document
          </div>
        </div>
      )}
      <div className="task-row">
        {/* First Half */}
        <div className="task-boxes">
          {isUploadFormOpen && (
            <div className="assign-new-task">
              <div className="assign-new-task-row">
                <div className="new-task-form-title">
                  Uploade Document
                  <MdAddTask style={{ marginLeft: "8px" }} />
                </div>
                <img
                  onClick={() => {
                    setIsUploadFormOpen(false);
                    setShowPendingDocsList(true);
                    setShowPendingDocDetails(false);
                  }}
                  width="48"
                  height="48"
                  src="https://img.icons8.com/fluency/48/delete-sign.png"
                  alt="delete-sign"
                  className="status-cross-symbol"
                />
              </div>

              <form className="new-task-form" onSubmit={taskHandleSubmit}>
                <div className="new-task-row">
                  <div className="new-task-row-input">
                    <label htmlFor="title">
                      Task Title:<span className="red-imp">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      className="new-task-title"
                      placeholder="e.g., Summer Sale Campaign"
                      value={taskForm.title}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="new-task-row-input imp">
                    <label htmlFor="document">
                      Upload file (PDF, Word, PPT, Excel, Text formats only):
                      <span className="red-imp">*</span>
                    </label>
                    <input
                      type="file"
                      name="document"
                      className={`img-input ${
                        fileValidation.isValid === true
                          ? "file-valid"
                          : fileValidation.isValid === false
                          ? "file-invalid"
                          : ""
                      }`}
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                      required
                      onChange={handleDocumentChange}
                    />
                  </div>

                  <div className="new-task-row-input">
                    <label htmlFor="description">Description:</label>
                    <textarea
                      name="description"
                      className="new-task-des"
                      placeholder="e.g., Focus on young users, fun tone, eco messaging"
                      value={taskForm.description}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>

                  <div className="checkbox-wrapper-51">
                    <label htmlFor="cbx-51" className="docviewer-wrapper">
                      <input
                        type="checkbox"
                        name="DocViewer"
                        checked={taskForm.DocViewer}
                        onChange={handleInputChange}
                        id="cbx-51"
                      />
                      <span className="toggle">
                        <span>
                          <svg width="10px" height="10px" viewBox="0 0 10 10">
                            <path d="M5,1 L5,1 C2.790861,1 1,2.790861 1,5 L1,5 C1,7.209139 2.790861,9 5,9 L5,9 C7.209139,9 9,7.209139 9,5 L9,5 C9,2.790861 7.209139,1 5,1 L5,9 L5,1 Z"></path>
                          </svg>
                        </span>
                      </span>
                      <span className="check-train-space"></span>
                      <span className="check-if-train-t">Check If it is Training Document</span>
                    </label>
                  </div>
                </div>

                <div className="new-task-row-submit">
                  <button
                    type="submit"
                    className="new-task-row-submit-btn"
                    disabled={
                      submitStatus === "submitting" ||
                      submitStatus === "submitted"
                    }
                  >
                    {submitStatus === "submitting"
                      ? "Submitting..."
                      : submitStatus === "submitted"
                      ? "Submitted"
                      : "Submit"}
                    <VscTasklist
                      style={{
                        marginLeft: "8px",
                        height: "30px",
                        width: "30px",
                      }}
                    />
                  </button>
                </div>
              </form>
            </div>
          )}

          {showPendingDocsList && (
            <div className="task-arrows">
              <img
                src={backicn}
                alt=""
                className="assign-task-btn back"
                onClick={pendingDocuments.length > 4 ? scrollLeft : undefined}
                style={{
                  visibility: pendingDocuments.length > 4 ? 'visible' : 'hidden',
                  pointerEvents: pendingDocuments.length > 4 ? 'auto' : 'none'
                }}
              />
              {hasPendingDocuments ? (
                <>
                  <div className="task-row-container" ref={scrollRef}>
                    {pendingDocuments.map((doc) => (
                      <div className="box" key={doc._id}>
                        <div className="cart-task-box-wrapper">
                          <div
                            className="process-cart-task-box process-doc-bg"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setShowPendingDocDetails(true);
                              setShowPendingDocsList(false);
                            }}
                          >
                            <div className="top-id-card">
                              <div className="name-task">Doc id:</div>
                              <div className="task-number">#{doc.DocId}</div>
                            </div>
                            <div className="process-bottom-title-card">
                              <div className="process-task-box-title">
                                {doc.title}
                              </div>
                              <button
                                className="process-cart-task-btn"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setShowPendingDocDetails(true);
                                  setShowPendingDocsList(false);
                                }}
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="temp-row-container tsk">
                  <div className="temp-title">
                    Click above to add Documents!
                  </div>
                  <div className="temp-img"> </div>
                </div>
              )}
              <img
                src={nexticn}
                alt=""
                className="assign-task-btn next"
                onClick={pendingDocuments.length > 4 ? scrollRight : undefined}
                style={{
                  visibility: pendingDocuments.length > 4 ? 'visible' : 'hidden',
                  pointerEvents: pendingDocuments.length > 4 ? 'auto' : 'none'
                }}
              />
            </div>
          )}
          {showPendingDocDetails && (
            <div className="doc-show-status-container">
              <div className="close-button-row">
                <img
                  onClick={() => {
                    setShowPendingDocsList(true);
                    setShowPendingDocDetails(false);
                  }}
                  width="48"
                  height="48"
                  src="https://img.icons8.com/fluency/48/delete-sign.png"
                  alt="delete-sign"
                  className="status-cross-symbol"
                />
              </div>
              <div className="status-top-floor">
                <div className="status-top-floor-row-1">
                  <div className="status-col-1">
                    <div className="status-row remove-padding">
                      <div className="status-task-id">
                        <div className="task-id for-gradient-font">Doc ID</div>
                        <div className="task-id-number">
                          #{selectedDoc?.DocId}
                        </div>
                      </div>

                      <div className="status-task-title">
                        <div className="task-title-status">Document Title</div>
                        <div className="task-title-name-status">
                          {selectedDoc?.title}
                        </div>
                      </div>

                      <div className="status-task-des">
                        <div className="task-title-status">Description</div>
                        <div className="task-title-name-status doc-description">
                          {selectedDoc?.description}
                        </div>
                      </div>
                      <div className="doc-view-btn">
                        <button className="process-cart-task-btn">
                          <a
                            href={selectedDoc.linkformatedocument}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </a>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="task-row">
        {/* Second Half */}
        <div className="approve-title"> Training Document 📖</div>
        {showApprovedDocsList && (
          <>
            <div className="task-approved-row">
              <div className="task-arrows">
                <img
                  src={backicn}
                  alt=""
                  className="assign-task-btn back"
                  onClick={approvedDocuments.length > 4 ? approvedScrollLeft : undefined}
                  style={{
                    visibility: approvedDocuments.length > 4 ? 'visible' : 'hidden',
                    pointerEvents: approvedDocuments.length > 4 ? 'auto' : 'none'
                  }}
                />
                {hasApprovedDocuments ? (
                  <div
                    className="task-approved-row-container"
                    ref={approvedScrollRef}
                  >
                    {approvedDocuments.map((doc) => (
                      <div className="box" key={doc._id}>
                        <div className="cart-task-box-wrapper">
                          <div
                            className="process-cart-task-box training-doc-bg"
                            onClick={() => {
                              setCurrentSelectedApprovedDoc(doc);
                              setShowApprovedDocDetails(true);
                              setShowApprovedDocsList(false);
                            }}
                          >
                            <div className="top-id-card">
                              <div className="name-task">Doc id:</div>
                              <div className="task-number">#{doc.DocId}</div>
                            </div>
                            <div className="process-bottom-title-card">
                              <div className="process-task-box-title">
                                {doc.title}
                              </div>
                              <button
                                className="process-cart-task-btn"
                                onClick={() => {
                                  setCurrentSelectedApprovedDoc(doc);
                                  setShowApprovedDocDetails(true);
                                  setShowApprovedDocsList(false);
                                }}
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="temp-row-container yet">
                    <div className="yet-temp-img"> </div>
                    <div className="temp-title">
                      Click above to add Documents Check it!
                    </div>
                  </div>
                )}

                <img
                  src={nexticn}
                  alt=""
                  className="assign-task-btn next"
                  onClick={approvedDocuments.length > 4 ? approvedScrollRight : undefined}
                  style={{
                    visibility: approvedDocuments.length > 4 ? 'visible' : 'hidden',
                    pointerEvents: approvedDocuments.length > 4 ? 'auto' : 'none'
                  }}
                />
              </div>
            </div>
          </>
        )}
        {showApprovedDocDetails && (
          <div className="doc-show-status-container">
            <div className=" close-button-row">
              <img
                onClick={() => {
                  setShowApprovedDocDetails(false);
                  setShowApprovedDocsList(true);
                }}
                width="48"
                height="48"
                src="https://img.icons8.com/fluency/48/delete-sign.png"
                alt="delete-sign"
                className="status-cross-symbol"
              />
            </div>
            <div className="status-top-floor">
              <div className="status-top-floor-row-1">
                <div className="status-col-1">
                  <div className="status-row remove-padding">
                    <div className="status-task-id">
                      <div className="task-id for-gradient-font">Doc ID</div>
                      <div className="task-id-number">
                        #{currentSelectedApprovedDoc?.DocId}
                      </div>
                    </div>

                    <div className="status-task-title">
                      <div className="task-title-status">Document Title</div>
                      <div className="task-title-name-status">
                        {currentSelectedApprovedDoc?.title}
                      </div>
                    </div>

                    <div className="status-task-des">
                      <div className="task-title-status">Description</div>
                      <div className="task-title-name-status doc-description">
                        {currentSelectedApprovedDoc?.description}
                      </div>
                    </div>
                    <div className="doc-view-btn">
                      <button className="process-cart-task-btn">
                        <a
                          href={currentSelectedApprovedDoc.linkformatedocument}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download{" "}
                        </a>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={500}
        hideProgressBar={false}
        newestOnTop={true}
        transition={Zoom}
      />
    </div>
  );
}
